import React from 'react';
import { Box, Button, useMediaQuery } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

export interface TimeNavigationButtonsProps {
  currentTime: Date;
  onTimeChange: (hours: number) => void;
  isMobile: boolean;
  minTime: Date;
}

const TimeNavigationButtons: React.FC<TimeNavigationButtonsProps> = ({
  currentTime,
  onTimeChange,
  isMobile,
  minTime
}) => {
  // Media queries to determine how many buttons to show
  // Show 2 buttons per side (4h and 15m) in portrait orientation
  // Show 3 buttons per side (4h, 1h, 15m) in landscape orientation
  // Only applies when isMobile is true (already below sm breakpoint of 600px)
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const showOnly2Buttons = isMobile && isPortrait;

  // Always get the latest UTC time
  const getMaxTime = () => new Date();
  const MAX_TIME = getMaxTime();

  // Check if we can go backward/forward
  const canGoBack15Min = new Date(currentTime.getTime() - 15 * 60 * 1000) >= minTime;
  const canGoBack1Hr = new Date(currentTime.getTime() - 60 * 60 * 1000) >= minTime;
  const canGoBack4Hr = new Date(currentTime.getTime() - 4 * 60 * 60 * 1000) >= minTime;
  const MIN_TIME_TO_ENABLE = 60 * 1000;
  const canGoForward = (MAX_TIME.getTime() - currentTime.getTime()) > MIN_TIME_TO_ENABLE;

  const buttonStyles = isMobile
    ? {
        minWidth: '70px',
        px: 1,
        py: 0.5,
        fontSize: '0.9rem',
        '& .MuiSvgIcon-root': {
          fontSize: '1rem'
        }
      }
    : {
        minWidth: 'auto',
        px: 1.8,
        py: 1.2,
        fontSize: '1.08rem',
        '& .MuiSvgIcon-root': {
          fontSize: '1.44rem'
        }
      };

  const containerStyles = isMobile
    ? {
        display: 'flex',
        flexDirection: 'row' as const,
        gap: 1,
        width: '100%',
        justifyContent: 'center',
      }
    : {
        display: 'flex',
        alignItems: 'center',
        gap: 2
      };

  return (
    <Box sx={containerStyles}>
      {/* Back 4 hours */}
      <Button
        className="no-uppercase"
        onClick={() => onTimeChange(-4)}
        disabled={!canGoBack4Hr}
        variant="contained"
        startIcon={<KeyboardDoubleArrowLeftIcon />}
        size={isMobile ? "small" : undefined}
        sx={{
          ...buttonStyles,
          opacity: canGoBack4Hr ? 1 : 0.5
        }}
      >
        -4h
      </Button>

      {/* Back 1 hour - hide on portrait only */}
      {!showOnly2Buttons && (
        <Button
          className="no-uppercase"
          onClick={() => onTimeChange(-1)}
          disabled={!canGoBack1Hr}
          variant="contained"
          startIcon={<KeyboardArrowLeftIcon />}
          size={isMobile ? "small" : undefined}
          sx={{
            ...buttonStyles,
            opacity: canGoBack1Hr ? 1 : 0.5
          }}
        >
          -1h
        </Button>
      )}

      {/* Back 15 minutes */}
      <Button
        className="no-uppercase"
        onClick={() => onTimeChange(-0.25)}
        disabled={!canGoBack15Min}
        variant="contained"
        startIcon={<KeyboardArrowLeftIcon />}
        size={isMobile ? "small" : undefined}
        sx={{
          ...buttonStyles,
          opacity: canGoBack15Min ? 1 : 0.5
        }}
      >
        -15m
      </Button>

      {/* Forward 15 minutes */}
      <Button
        className="no-uppercase"
        onClick={() => onTimeChange(0.25)}
        disabled={!canGoForward}
        variant="contained"
        endIcon={<KeyboardArrowRightIcon />}
        size={isMobile ? "small" : undefined}
        sx={{
          ...buttonStyles,
          opacity: canGoForward ? 1 : 0.5
        }}
      >
        +15m
      </Button>

      {/* Forward 1 hour - hide on portrait only */}
      {!showOnly2Buttons && (
        <Button
          className="no-uppercase"
          onClick={() => onTimeChange(1)}
          disabled={!canGoForward}
          variant="contained"
          endIcon={<KeyboardArrowRightIcon />}
          size={isMobile ? "small" : undefined}
          sx={{
            ...buttonStyles,
            opacity: canGoForward ? 1 : 0.5
          }}
        >
          +1h
        </Button>
      )}

      {/* Forward 4 hours */}
      <Button
        className="no-uppercase"
        onClick={() => onTimeChange(4)}
        disabled={!canGoForward}
        variant="contained"
        endIcon={<KeyboardDoubleArrowRightIcon />}
        size={isMobile ? "small" : undefined}
        sx={{
          ...buttonStyles,
          opacity: canGoForward ? 1 : 0.5
        }}
      >
        +4h
      </Button>
    </Box>
  );
};

export default TimeNavigationButtons;